import React from "react";
import { arrayIntersection } from "../../../../lib/Util";

import type { IOrderProducedModelEx } from "../reducers/linesOrders";
import type { PlanningTableColumnId } from "../../../ViewsCommon";

const ORANGE_COLOR = "hsl(30deg 100% 73% / 50%)";
const GREEN_COLOR = "hsl(103deg 92% 60% / 50%)";
const RED_COLOR = "hsl(0deg 100% 50% / 30%)";

/**
 * Conditional styling logic that wraps the cell value with a styled span element.
 */
export default class ConditionalStyles {
    /**
     * Adds conditional styling to a cell based on the order and column id.
     */
    static add(
        column_id: PlanningTableColumnId,
        order: IOrderProducedModelEx,
        sim: { start_time?: number; end_time?: number }
    ): React.CSSProperties | null {
        let styles: React.CSSProperties | null = null;
        const { start_time, end_time } = sim || {};

        switch (column_id) {
            // Highlights the value if the `earliest_end` is after the component test deadline
            case "custom_lts-lohmann_comp_test_deadline": {
                const comp_test_deadline = order.tags["custom_lts-lohmann_comp_test_deadline"];

                if (typeof comp_test_deadline === "string") {
                    if (end_time !== undefined && new Date(end_time) > new Date(comp_test_deadline)) {
                        styles = { backgroundColor: ORANGE_COLOR };
                    }
                }

                break;
            }

            // Highlights the value if the status includes the printed process order code "PRT"
            case "custom_lts-lohmann_syst_status": {
                const sys_status = order.tags["custom_lts-lohmann_syst_status"];
                const codes_to_highlight = ["PRT", "DRUC"];

                if (typeof sys_status === "string") {
                    const syst_status_codes = sys_status.split(/\s+/);
                    if (arrayIntersection(syst_status_codes, codes_to_highlight).length > 0) {
                        styles = { backgroundColor: GREEN_COLOR };
                    }
                }

                break;
            }

            // Highlights the value if current_start - bom_availability_date is negative
            case "bom_availability_days": {
                const bom_availability_date = Number(order.tags.bom_availability_date);

                if (bom_availability_date && start_time) {
                    if (start_time - bom_availability_date < 0) {
                        styles = { backgroundColor: RED_COLOR };
                    }
                }

                break;
            }

            // Highlights the value if the required_deadline - end_time is negative
            case "required_deadline_diff": {
                const required_deadline = Number(order.tags.required_deadline);

                if (required_deadline && end_time) {
                    if (required_deadline - end_time < 0) {
                        styles = { backgroundColor: RED_COLOR };
                    }
                }
            }
        }

        return styles;
    }
}
